import React, { FC, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Report, useGetScalingSize } from '@ui/Report';
import { ReportEditablePageHeader as EditablePageHeader } from './EditablePageHeader';
import type { IProposal } from '@shared/models/proposals.model';
import type { Project } from '@shared/models/project.model';
import {
  useGetProposalColor,
  useProposalsBarChartValues,
} from '@features/proposals/use-proposals.hooks';
import {
  barScaledCss,
  getProposalsOverviewBarFootRuleWidthCss,
  getProposalsOverviewBarGapFromVarCss,
  getProposalsOverviewBarWidthCss,
  getProposalsOverviewBarWidthThumbnailCss,
  PROPOSALS_OVERVIEW_NAME_BAND,
  PROPOSALS_OVERVIEW_TARGET_LINE,
  REPORT_BAR_WIDTH_VAR,
} from '@features/proposals/proposals-overview-bar-layout.helpers';
import {
  getReportSummaryTargetLabelBottomCss,
  REPORT_SUMMARY_BAR_PLOT,
  REPORT_SUMMARY_DEFAULT_TITLE,
  REPORT_SUMMARY_TARGET_LABEL,
} from './report-summary.constants';
import { BASELINE_COLOR } from '@shared/helpers/proposals/proposal-colors.constants';
import { getProject, useUpdateProject } from '@client/store/project';
import { getSelectedVersion, useSelectedVersion } from '@client/store/ui';
import { updateBuildingVersionProperties } from '@shared/helpers/project.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { Icon } from '@ui/Icon/Icon';
import VerticalBar from '@features/proposals/ProposalsOverview/VerticalBar';
import {
  getReportSummaryBarDiffValue,
  getReportSummaryTargetLineBottomPercent,
  getReportSummaryVisibleTarget,
} from './report-summary.helpers';

const CHART_BG = '#F2F0ED';
const SUMMARY_BODY_PLACEHOLDER = 'Add summary text\u2026';

const { Text } = Report;

type ReportSummaryProps = {
  proposals: IProposal[];
  maxCo2e: number;
  target: Project['target'];
};

export const ReportSummary: FC<ReportSummaryProps> = ({
  proposals,
  maxCo2e,
  target,
}) => {
  const selectedVersion = useSelectedVersion();
  const updateProject = useUpdateProject();
  const getProposalColor = useGetProposalColor();
  const getScalingSize = useGetScalingSize();

  const barWidth = useMemo(
    () => getProposalsOverviewBarWidthCss(proposals.length),
    [proposals.length],
  );
  const barWidthThumbnail = useMemo(
    () => getProposalsOverviewBarWidthThumbnailCss(proposals.length),
    [proposals.length],
  );

  const handleSaveVersionField = useCallback(
    (field: 'report_summary_title' | 'report_summary_col1', value: string) => {
      const version = getSelectedVersion();
      if (!version) return;
      const next = updateBuildingVersionProperties(getProject(), {
        id: version.id,
        [field]: value || undefined,
      });
      // TODO(replay): express as ProjectChange so a 422 race can be replayed.
      updateProject({ kind: 'snapshot', project: next });
    },
    [updateProject],
  );

  const bodyFields = useMemo(
    () => [
      {
        id: 'col1',
        text: selectedVersion?.report_summary_col1,
        placeholder: SUMMARY_BODY_PLACEHOLDER,
        onSave: (value: string) =>
          handleSaveVersionField('report_summary_col1', value),
      },
    ],
    [handleSaveVersionField, selectedVersion?.report_summary_col1],
  );

  const barFootRuleWidth = useMemo(
    () => getProposalsOverviewBarFootRuleWidthCss(proposals.length),
    [proposals.length],
  );

  /** Target line height within the bar plot — same ratio as {@link Proposals}. Zero is stored but not shown. */
  const visibleTarget = getReportSummaryVisibleTarget(target);
  const targetLineBottomPercent = getReportSummaryTargetLineBottomPercent(
    visibleTarget,
    maxCo2e,
  );

  const { classes } = useStyles({
    barWidth,
    barWidthThumbnail,
    barFootRuleWidth,
    getScalingSize,
  });

  return (
    <Report.Page padding={0} verticalAlign="stretch" hideFooter>
      <Box className={classes.barWidthVars}>
        <EditablePageHeader
          resetKey={selectedVersion?.id ?? 'none'}
          defaultTitle={REPORT_SUMMARY_DEFAULT_TITLE}
          titleText={selectedVersion?.report_summary_title}
          onSaveTitle={(value) =>
            handleSaveVersionField('report_summary_title', value)
          }
          bodyFields={bodyFields}
          titleFullWidth
        />

        <Box className={classes.barPlot}>
          {targetLineBottomPercent !== null && (
            <Box
              className={classes.warmPlate}
              sx={{
                height: `calc(${targetLineBottomPercent}% + ${barScaledCss(2)})`,
              }}
            />
          )}

          {targetLineBottomPercent !== null && (
            <Box
              className={classes.targetLine}
              sx={{
                bottom: `calc(${targetLineBottomPercent}% - ${barScaledCss(PROPOSALS_OVERVIEW_TARGET_LINE.offsetPx)})`,
              }}
            />
          )}

          <Box className={classes.barFootRule} />

          <Box className={classes.barsRow}>
            {proposals.map((proposal) => (
              <SummaryBar
                key={proposal.id}
                classes={classes}
                proposal={proposal}
                color={
                  proposal.colorIndex === -1
                    ? BASELINE_COLOR
                    : getProposalColor(proposal)
                }
                co2ePerGfaMax={maxCo2e}
                target={target}
              />
            ))}
          </Box>
        </Box>

        {visibleTarget !== undefined && targetLineBottomPercent !== null && (
          <Box
            className={classes.targetLabel}
            sx={{
              bottom: getReportSummaryTargetLabelBottomCss(
                targetLineBottomPercent,
              ),
            }}
          >
            <Text
              as="span"
              fontWeight={700}
              fontSize={REPORT_SUMMARY_TARGET_LABEL.fontSize}
              color={REPORT_SUMMARY_TARGET_LABEL.color}
              sx={{
                lineHeight: 1.3,
                opacity: 0.8,
                fontFamily: REPORT_SUMMARY_TARGET_LABEL.fontFamily,
              }}
            >
              Target:
            </Text>
            <Box className={classes.targetLabelValueGroup}>
              <Box sx={{ opacity: 0.4 }}>
                <Icon
                  category="costEmission"
                  icon="kgCO2e"
                  size={getScalingSize(REPORT_SUMMARY_TARGET_LABEL.iconSize)}
                />
              </Box>
              <Text
                as="span"
                fontWeight={900}
                fontSize={REPORT_SUMMARY_TARGET_LABEL.fontSize}
                color={REPORT_SUMMARY_TARGET_LABEL.color}
                sx={{
                  lineHeight: 1.3,
                  opacity: 0.8,
                  fontFamily: REPORT_SUMMARY_TARGET_LABEL.fontFamily,
                }}
              >
                {getDisplayNumber(visibleTarget, { notation: 'compact' })}
              </Text>
            </Box>
          </Box>
        )}

        <Box className={classes.namesRow}>
          {proposals.map((proposal) => (
            <SummaryBarName
              key={proposal.id}
              classes={classes}
              proposal={proposal}
            />
          ))}
        </Box>
      </Box>
    </Report.Page>
  );
};

const useStyles = makeStyles<{
  barWidth: string;
  barWidthThumbnail: string;
  barFootRuleWidth: string;
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()(
  (
    _theme,
    { barWidth, barWidthThumbnail, barFootRuleWidth, getScalingSize },
  ) => ({
    barWidthVars: {
      [REPORT_BAR_WIDTH_VAR as string]: barWidth,
      '[data-thumbnail] &': {
        [REPORT_BAR_WIDTH_VAR as string]: barWidthThumbnail,
      },
    },
    barPlot: {
      position: 'absolute',
      top: `${REPORT_SUMMARY_BAR_PLOT.topPercent}%`,
      left: 0,
      right: 0,
      height: `${REPORT_SUMMARY_BAR_PLOT.heightPercent}%`,
      overflow: 'visible',
      zIndex: 1,
    },
    warmPlate: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: CHART_BG,
      pointerEvents: 'none',
      zIndex: 0,
    },
    targetLine: {
      position: 'absolute',
      left: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      height: barScaledCss(PROPOSALS_OVERVIEW_TARGET_LINE.hitHeightPx),
      pointerEvents: 'none',
      zIndex: 1,
      '&::after': {
        content: '""',
        display: 'block',
        width: '100%',
        borderTop: `${barScaledCss(PROPOSALS_OVERVIEW_TARGET_LINE.strokePx)} dashed rgba(0,0,0,0.25)`,
      },
    },
    targetLabel: {
      position: 'absolute',
      right: `${REPORT_SUMMARY_TARGET_LABEL.offsetRightPercent}%`,
      display: 'flex',
      alignItems: 'center',
      gap: getScalingSize(REPORT_SUMMARY_TARGET_LABEL.outerGap),
      pointerEvents: 'none',
      zIndex: 4,
    },
    targetLabelValueGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: getScalingSize(REPORT_SUMMARY_TARGET_LABEL.innerGap),
    },
    barFootRule: {
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: barFootRuleWidth,
      borderTop: `${barScaledCss(1)} solid rgba(0,0,0,0.1)`,
      pointerEvents: 'none',
      zIndex: 3,
    },
    barsRow: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: getProposalsOverviewBarGapFromVarCss(),
      height: '100%',
      minHeight: 0,
      overflow: 'visible',
      zIndex: 2,
    },
    namesRow: {
      position: 'absolute',
      top: `${REPORT_SUMMARY_BAR_PLOT.feetPercent}%`,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
      gap: getProposalsOverviewBarGapFromVarCss(),
      overflow: 'visible',
    },
    barColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: `var(${REPORT_BAR_WIDTH_VAR})`,
      flexShrink: 0,
      height: '100%',
      position: 'relative',
    },
    barSlot: {
      position: 'relative',
      flex: 1,
      width: '100%',
      minHeight: 0,
    },
    nameColumn: {
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'center',
      width: `var(${REPORT_BAR_WIDTH_VAR})`,
    },
    nameTab: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: `${PROPOSALS_OVERVIEW_NAME_BAND.tabWidthFactor * 100}%`,
      minHeight: barScaledCss(PROPOSALS_OVERVIEW_NAME_BAND.heightPx),
      paddingLeft: barScaledCss(PROPOSALS_OVERVIEW_NAME_BAND.paddingXPx),
      paddingRight: barScaledCss(PROPOSALS_OVERVIEW_NAME_BAND.paddingXPx),
    },
    proposalName: {
      lineHeight: 1,
      textAlign: 'center',
      opacity: 0.6,
      fontSize: barScaledCss(PROPOSALS_OVERVIEW_NAME_BAND.fontSizePx),
    },
    proposalNameBaseline: {
      fontStyle: 'italic',
      fontWeight: 400,
    },
    proposalNameDefault: {
      fontWeight: 700,
    },
  }),
);

type SummaryBarProps = {
  classes: ReturnType<typeof useStyles>['classes'];
  proposal: IProposal;
  color: string;
  co2ePerGfaMax: number;
  target: Project['target'];
};

const SummaryBar: FC<SummaryBarProps> = ({
  classes,
  proposal,
  color,
  co2ePerGfaMax,
  target,
}) => {
  const { co2eChartData, co2ePerGFA } = useProposalsBarChartValues(proposal.id);
  const diffValue = getReportSummaryBarDiffValue({
    target,
    co2ePerGfa: co2ePerGFA,
  });

  return (
    <Box className={classes.barColumn}>
      <Box className={classes.barSlot}>
        <VerticalBar
          fluid
          color={color}
          co2eChartData={co2eChartData}
          co2ePerGFA={co2ePerGFA}
          co2ePerGfaMax={co2ePerGfaMax}
          diffValue={diffValue}
        />
      </Box>
    </Box>
  );
};

type SummaryBarNameProps = {
  classes: ReturnType<typeof useStyles>['classes'];
  proposal: IProposal;
};

const SummaryBarName: FC<SummaryBarNameProps> = ({ classes, proposal }) => {
  const isBaseline = proposal.colorIndex === -1;

  return (
    <Box className={classes.nameColumn}>
      <Box className={classes.nameTab}>
        <Typography
          noWrap
          className={`${classes.proposalName} ${
            isBaseline
              ? classes.proposalNameBaseline
              : classes.proposalNameDefault
          }`}
        >
          {proposal.name}
        </Typography>
      </Box>
    </Box>
  );
};
